// Composant parent pour la gestion des types de licences (orchestrateur)
import { useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import AdminInfoBanner from '../AdminInfoBanner';
import { LicenseTypeForm } from './LicenseTypeForm';
import { LicenseTypeListItem } from './LicenseTypeListItem';
import { useLicenseTypes } from './useLicenseTypes';
import { useLicenseTypeForm } from './useLicenseTypeForm';

interface LicenseTypeEditorProps {
  onRefresh?: () => void;
}

export default function LicenseTypeEditor({ onRefresh }: LicenseTypeEditorProps) {
  const {
    licenseTypes,
    loading,
    loadLicenseTypes,
    deleteLicenseType,
    toggleActive
  } = useLicenseTypes(onRefresh);

  const {
    editingType,
    isCreating,
    formData,
    saving,
    setFormData,
    resetForm,
    startEdit,
    startCreate,
    handleSave
  } = useLicenseTypeForm(() => {
    loadLicenseTypes();
    onRefresh?.();
  });

  useEffect(() => {
    loadLicenseTypes();
  }, [loadLicenseTypes]);

  const handleSaveAndClose = async () => {
    await handleSave();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Banner d'information */}
      <AdminInfoBanner
        title="Gestion des Types de Licences"
        description="Cette interface vous permet de créer, modifier et gérer tous les types de licences disponibles à l'achat sur votre site. Les modifications sont appliquées en temps réel."
      />

      {/* En-tête avec bouton d'ajout */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Types de licences
        </h2>
        <button
          onClick={startCreate}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Nouveau type
        </button>
      </div>

      {/* Formulaire de création/édition */}
      {(isCreating || editingType) && (
        <LicenseTypeForm
          formData={formData}
          setFormData={setFormData}
          isCreating={isCreating}
          saving={saving}
          onSave={handleSaveAndClose}
          onCancel={resetForm}
        />
      )}

      {/* Liste des types de licences */}
      <div className="grid gap-4">
        {licenseTypes.map((licenseType) => (
          <LicenseTypeListItem
            key={licenseType.id}
            licenseType={licenseType}
            onEdit={startEdit}
            onDelete={deleteLicenseType}
            onToggleActive={toggleActive}
          />
        ))}
      </div>

      {licenseTypes.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">
            Aucun type de licence trouvé.
          </p>
        </div>
      )}
    </div>
  );
}
